import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

import { IS_STAGING, PATH } from '@constants';
import { platformAppsTemplateSelector } from '@selectors';
import { useSelector } from '@hooks';
import { capitalize } from '@utils';

import { Helmet, Icon, Render } from '@components';
import { Layout } from '@components/Layouts';
import { AppsTabs } from '@components/Ui';
import { AppsViewType } from '@components/Ui/Apps';

const TABS = [
  {
    title: 'All apps',
    path: PATH.APPS,
  },
  {
    title: 'Installed apps',
    path: PATH.APPS_INSTALLED,
  },
  {
    title: 'Legacy apps',
    path: PATH.APPS_LEGACY,
  },
];

if (!IS_STAGING) {
  TABS.push({ title: 'Dev install', path: PATH.APPS_DEV_INSTALL });
  TABS.push({ title: 'Dev schema', path: PATH.APPS_DEV_SCHEMA });
}

export const PlatformAppsLayout = () => {
  const { pathname } = useLocation();
  const { appName } = useParams();
  const navigate = useNavigate();

  const template = useSelector(platformAppsTemplateSelector(appName!));

  const [title, setTitle] = useState('');

  const formattedTitle = capitalize(title);
  const isAllAppsPage = pathname === PATH.APPS;

  useEffect(() => {
    // https://linear.app/apolo/issue/ENG-751/add-apps-page-filtered-by-template-name
    if (appName) {
      setTitle('Installed apps');
    }
  }, [appName]);

  return (
    <Layout title={formattedTitle} backNavigation={Boolean(appName)}>
      <Helmet title={formattedTitle} />
      <Layout.Content>
        <div className="mb-14 flex items-center justify-between">
          <Render if={appName}>
            <div className="flex items-center justify-center gap-1.5 rounded-full bg-rebecca px-6 py-2 capitalize text-white">
              {template?.title || appName}

              <Icon
                icon={faXmark}
                className="cursor-pointer text-[20px]"
                onClick={() =>
                  // history.length > 2 to ensure there is a meaningful navigation history.
                  window.history.length > 2 ? navigate(-1) : navigate(PATH.APPS)
                }
              />
            </div>
          </Render>

          <Render if={!appName}>
            <AppsTabs tabs={TABS} setTitle={setTitle} />
          </Render>

          <Render if={!isAllAppsPage}>
            <AppsViewType />
          </Render>
        </div>

        <Outlet />
      </Layout.Content>
    </Layout>
  );
};
